import { Box } from '@mui/material';
import UPDayPickerDisplay from 'components/day-picker-display';
import AbsenceIndicator from 'modules/contract-processing/components/AbsenceIndicator';
import RegistrationStatusBadge from 'modules/contract-processing/components/RegistrationStatusBadge';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CONTRACTS__CONFIRMED,
  CONTRACTS__DATE,
  CONTRACTS__TO_BE_CONFIRMED,
  CONTRACTS__WORKING_TIMES,
} from 'translations/constants';
import { ReactComponent as ConfirmedProcessingIcon } from '../../../assets/icons/ConfirmedProcessingIcon.svg';
import {
  ColumnTitleRow,
  SectionCard,
  SectionDivider,
  SectionTitle,
  SubSectionTitle,
  ThreeColumnLayout,
  TimeColumnContainer,
  WorkingTimesHeaderRow,
} from '../styles';
import { ContractDetailPanelData, DetailPanelFormValues } from '../types';
import AbsenceCostDeclarationSection from './AbsenceCostDeclarationSection';
import PlannedTimeColumn from './PlannedTimeColumn';
import RegisteredTimeColumn from './RegisteredTimeColumn';
import ToBeConfirmedColumn from './ToBeConfirmedColumn';

interface WorkingTimesSectionProps {
  data: ContractDetailPanelData;
  form: UseFormReturn<DetailPanelFormValues>;
  isNoShowReasonUnavailable: boolean;
  readOnly?: boolean;
}

const WorkingTimesSection = (props: WorkingTimesSectionProps) => {
  const { data, form, isNoShowReasonUnavailable, readOnly } = props;
  const { t } = useTranslation();
  const watchedReason = form.watch('reasonForNoShow');
  const isNoShow = !!watchedReason;

  return (
    <SectionCard>
      <WorkingTimesHeaderRow>
        <SectionTitle>{t(CONTRACTS__WORKING_TIMES)}</SectionTitle>
        {data.hasTimeRegistration && (
          <RegistrationStatusBadge
            hasTimeRegistration={data.hasTimeRegistration}
            status={data.timeRegistrationStatus}
          />
        )}
      </WorkingTimesHeaderRow>
      <SectionDivider />
      <Box width={200}>
        <UPDayPickerDisplay label={t(CONTRACTS__DATE)} day={new Date(data.date)} id="detail-panel-date" />
      </Box>
      <ThreeColumnLayout>
        <PlannedTimeColumn
          hasPlanning={data.hasPlanning}
          startWork={data.plannedStartWork}
          endWork={data.plannedEndWork}
          startBreak={data.plannedStartBreak}
          endBreak={data.plannedEndBreak}
        />
        <RegisteredTimeColumn
          hasRegistration={data.hasRegistration}
          startTime={data.registeredStartTime}
          endTime={data.registeredEndTime}
          breaks={data.registeredBreaks}
          isStartTimeAdjusted={data.isStartTimeAdjusted}
          isEndTimeAdjusted={data.isEndTimeAdjusted}
        />
        {isNoShow ? (
          <TimeColumnContainer>
            <ColumnTitleRow>
              <ConfirmedProcessingIcon />
              <SubSectionTitle>{t(readOnly ? CONTRACTS__CONFIRMED : CONTRACTS__TO_BE_CONFIRMED)}</SubSectionTitle>
            </ColumnTitleRow>
            <Box display="flex" alignItems="center" height={48}>
              <AbsenceIndicator />
            </Box>
          </TimeColumnContainer>
        ) : (
          <ToBeConfirmedColumn form={form} readOnly={readOnly} />
        )}
      </ThreeColumnLayout>
      <SectionDivider />
      <AbsenceCostDeclarationSection
        form={form}
        noShowReasonDetail={data.noShowReasonDetail}
        isNoShowReasonUnavailable={isNoShowReasonUnavailable}
        readOnly={readOnly}
      />
    </SectionCard>
  );
};

WorkingTimesSection.defaultProps = {
  readOnly: false,
};

export default WorkingTimesSection;
